import React, { createContext, useCallback, useContext, useState } from 'react';
import { AccordionProps } from './Accordion';
import resolveConfig from 'tailwindcss/resolveConfig';
import tailwindConfig from '../../../tailwind.config';

interface AccordionContextProps {
	openPanels: Set<string>;
	onClickHandler: (clickedPanelId: string) => void;
	tailwindSmBreakpointValue: number;
}

const AccordionContext = createContext<AccordionContextProps>(
	{} as AccordionContextProps,
);

type AccordionProviderProps = AccordionProps;

export const AccordionProvider = (props: AccordionProviderProps) => {
	const { allowMultiple, onToggle } = props;

	const [openPanels, setOpenPanels] = useState<Set<string>>(new Set());

	// getting the tailwind mobile breakpoint for the expandPanelsDesktop condition
	let tailwindMobileBreakpoint: string =
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore - tailwind is confused
		resolveConfig(tailwindConfig).theme?.screens?.sm;
	tailwindMobileBreakpoint = tailwindMobileBreakpoint.replace('px', '');

	const breakpointValue = parseInt(tailwindMobileBreakpoint);

	const btnOnClick = useCallback(
		(clickedPanelId: string) => {
			setOpenPanels((prevOpenPanels) => {
				// Clone the set before manipulating it
				let nextOpenPanelsSet = new Set(prevOpenPanels);
				// Check if panel is already open
				if (nextOpenPanelsSet.has(clickedPanelId)) {
					// Remove it
					nextOpenPanelsSet.delete(clickedPanelId);
					onToggle?.(clickedPanelId, 'closed');
				} else {
					// If allowMultiple is true, add it
					if (allowMultiple) {
						nextOpenPanelsSet.add(clickedPanelId);
					} else {
						// If allowMultiple is false, replace the set with a new one containing only the clicked panel
						nextOpenPanelsSet = new Set([clickedPanelId]);
					}
					onToggle?.(clickedPanelId, 'opened');
				}
				// Return the set to update the state
				return nextOpenPanelsSet;
			});
		},
		[allowMultiple, onToggle],
	);

	return (
		<AccordionContext.Provider
			value={{
				openPanels,
				onClickHandler: btnOnClick,
				tailwindSmBreakpointValue: breakpointValue,
			}}
			{...props}
		/>
	);
};

export const useAccordionContext = () =>
	useContext<AccordionContextProps>(AccordionContext);
